import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Respuesta estándar MensajeResponse del backend: { message, object, status }.
 */
export interface MensajeResponse<T = any> {
    message?: string;
    object?: T;
    status?: boolean;
}

export interface GnbConfigDTO {
    accountNumber: string;
    ruc: string;
    companyName: string;
    documentType: string;
    environment: string;
}

export interface GnbValidationResponseDTO {
    validationStatus: 'FOUND' | 'PENDING' | 'TECHNICAL_ERROR';
    gnbStatus: string;
    eqSeq?: string;
    reportId?: string;
    amount?: number;
    currency?: string;
    acctSvcrRef?: string;
    debtorName?: string;
    creditorName?: string;
    debtorAccount?: string;
    creditorAccount?: string;
    debtorBankName?: string;
    creditorBankName?: string;
    errorCode?: string;
    errorMessage?: string;
    errorType?: string;
}

export type GnbValidationResult = GnbValidationResponseDTO;

export interface GnbMovementDTO {
    eqSeq?: string;
    comprobante?: string;
    ntryRef?: string;
    txId?: string;
    endToEndId?: string;
    amount?: number;
    currency?: string;
    cdtDbtInd?: string;
    bookingDate?: string;
    concept?: string;
    clienteEmisor?: string;
    acctSvcrRef?: string;
    debtorBankBic?: string;
    debtorBankName?: string;
    creditorBankBic?: string;
    creditorBankName?: string;
    hasDetail?: boolean;
    status?: string;
    embeddedDetail?: GnbValidationResponseDTO;
    detail?: GnbValidationResponseDTO;
}

export interface GnbMovementsReportDTO {
    rawResponse?: string;
    movements?: GnbMovementDTO[];
}

export interface GnbValidateByComprobanteRequest {
    comprobanteNumber: string;
    concept: 'SPI' | 'SIPAP';
    /** BIC de 8 caracteres del banco emisor (prefijo del TxId) para filtrar movimientos. */
    bicEmisor?: string;
    /** Monto de la transferencia: filtra primero por monto antes de buscar el comprobante. */
    monto?: number;
}

export interface GnbValidationRequest {
    eqSeq: string;
    accountNumber: string;
    ruc: string;
    companyName?: string;
    documentType?: string;
    concept: 'SPI' | 'SIPAP';
}

export interface GnbVerificacionRequest {
    monto: number;
    bicEmisor?: string;
    clienteEmisor: string;
    concept: 'SPI' | 'SIPAP';
}

/**
 * Bancos participantes (documentación GNB Business Connect, sección 3.1).
 * El campo indica el BANCO EMISOR: el banco desde el cual el cliente envió la
 * transferencia al banco GNB (y que emite el número de comprobante).
 * `bic` = BIC de 8 caracteres, usado para filtrar los movimientos (prefijo del TxId).
 * Solo GNB tiene validación automática integrada por ahora.
 */
export const GNB_BANKS = [
    { label: 'Banco GNB Paraguay S.A.E.C.A.', value: 'GNB', bic: 'BGNBPYPX' },
    { label: 'Financiera Ueno S.A.E.C.A. (Ueno Bank)', value: 'UENO', bic: 'COMAPYPA' },
    { label: 'Banco Atlas S.A.', value: 'ATLAS', bic: 'BNITPYPA' },
    { label: 'Banco BASA S.A.', value: 'BASA', bic: 'AMAMPYPA' },
    { label: 'Banco Central del Paraguay', value: 'BCP', bic: 'BCPAPYPX' },
    { label: 'Banco Continental S.A.E.C.A.', value: 'CONTINENTAL', bic: 'BCNAPYPA' },
    { label: 'Banco de la Nación Argentina', value: 'BNA', bic: 'NACNPYPA' },
    { label: 'Banco do Brasil S.A.', value: 'BRASIL', bic: 'BRASPYPX' },
    { label: 'Banco Familiar S.A.E.C.A.', value: 'FAMILIAR', bic: 'FAMIPYPA' },
    { label: 'Banco Itaú Paraguay S.A.', value: 'ITAU', bic: 'ITAUPYPA' },
    { label: 'Banco Nacional de Fomento', value: 'BNF', bic: 'BNFAPYPA' },
    { label: 'Banco para la Comercialización y Producción', value: 'BCOP', bic: 'BCOPPYPA' },
    { label: 'Banco Río S.A.E.C.A.', value: 'RIO', bic: 'BISAPYPE' },
    { label: 'Citibank N.A.', value: 'CITI', bic: 'CITIUS33' },
    { label: 'Interfisa Banco S.A.E.C.A.', value: 'INTERFISA', bic: 'IIFAPYPA' },
    { label: 'Solar Banco S.A.E.', value: 'SOLAR', bic: 'SOLAPYPA' },
    { label: 'Sudameris Bank S.A.E.C.A.', value: 'SUDAMERIS', bic: 'BSUDPYPX' },
    { label: 'Zeta Banco S.A.E.C.A.', value: 'ZETA', bic: 'FNXAPYPA' }
];

@Injectable({ providedIn: 'root' })
export class GnbService {
    private http = inject(HttpClient);
    private base = environment.apiUrl + 'gnb';

    getConfig(): Observable<MensajeResponse<GnbConfigDTO>> {
        return this.http.get<MensajeResponse<GnbConfigDTO>>(`${this.base}/config`);
    }

    validate(eqSeq: string): Observable<MensajeResponse<GnbValidationResponseDTO>> {
        const request: GnbValidationRequest = {
            eqSeq,
            accountNumber: '',
            ruc: '',
            concept: 'SPI'
        };
        return this.http.post<MensajeResponse<GnbValidationResponseDTO>>(`${this.base}/validate`, request);
    }

    validateByComprobante(request: GnbValidateByComprobanteRequest): Observable<MensajeResponse<GnbValidationResponseDTO>> {
        return this.http.post<MensajeResponse<GnbValidationResponseDTO>>(`${this.base}/validate-by-comprobante`, request);
    }

    validateTransfer(request: GnbValidationRequest): Observable<MensajeResponse<GnbValidationResponseDTO>> {
        return this.http.post<MensajeResponse<GnbValidationResponseDTO>>(`${this.base}/validate`, request);
    }

    /** Proceso unificado: monto + banco emisor + cliente emisor → estado + EQSeq. */
    verificar(request: GnbVerificacionRequest): Observable<MensajeResponse<GnbValidationResponseDTO>> {
        return this.http.post<MensajeResponse<GnbValidationResponseDTO>>(`${this.base}/verificar`, request);
    }

    getMovements(concept: string, comprobante?: string, bicEmisor?: string, monto?: number): Observable<MensajeResponse<GnbMovementsReportDTO>> {
        const params: any = { concept };
        if (comprobante) params.comprobante = comprobante;
        if (bicEmisor) params.bicEmisor = bicEmisor;
        if (monto != null && !isNaN(monto)) params.monto = monto;
        return this.http.get<MensajeResponse<GnbMovementsReportDTO>>(`${this.base}/movements`, { params });
    }

    /** Consulta movimientos con rango de fecha y hora exacto (desde/hasta). */
    getMovementsByRange(params: {
        concept: string;
        comprobante?: string;
        bicEmisor?: string;
        monto?: number;
        cdtDbtInd?: string;
        clienteEmisor?: string;
        fromDate?: string;
        fromTime?: string;
        toDate?: string;
        toTime?: string;
    }): Observable<MensajeResponse<GnbMovementsReportDTO>> {
        const qp: any = { concept: params.concept };
        if (params.comprobante) qp.comprobante = params.comprobante;
        if (params.bicEmisor) qp.bicEmisor = params.bicEmisor;
        if (params.monto != null && !isNaN(params.monto)) qp.monto = params.monto;
        if (params.cdtDbtInd) qp.cdtDbtInd = params.cdtDbtInd;
        if (params.clienteEmisor) qp.clienteEmisor = params.clienteEmisor;
        if (params.fromDate) qp.fromDate = params.fromDate;
        if (params.fromTime) qp.fromTime = params.fromTime;
        if (params.toDate) qp.toDate = params.toDate;
        if (params.toTime) qp.toTime = params.toTime;
        return this.http.get<MensajeResponse<GnbMovementsReportDTO>>(`${this.base}/movements/range`, { params: qp });
    }
}
